import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import FormDialog from '../../../../Components/Modal/modal';
import CommonTextField from '../../../../Components/TextField/textField';
import CommonDatePicker from '../../../../Components/Datepicker/datepicker';
import SearchDropdown from '../../../../Components/Dropdown/dropdown';
import { EMPTY_REQUEST_ROW } from '../../../../Constants/strings';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const normalizeText = (value) => String(value ?? '').trim();

const normalizeExperienceLabel = (value) => normalizeText(value)
  .replace(/\s*(yrs?|years?)$/i, '')
  .replace(/\s*-\s*/g, '-')
  .replace(/\s*\+\s*$/g, '+')
  .replace(/\s+/g, ' ');

const getExperienceSortParts = (value) => {
  const label = normalizeExperienceLabel(value).toLowerCase();
  const compactLabel = label.replace(/[\s_-]/g, '');

  if (compactLabel === 'junior') return { group: 1, start: 0, end: 0 };
  if (compactLabel === 'midlevel') return { group: 1, start: 1, end: 1 };
  if (compactLabel === 'senior') return { group: 1, start: 2, end: 2 };

  const rangeMatch = label.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    return { group: 0, start: Number(rangeMatch[1]), end: Number(rangeMatch[2]) };
  }

  const plusMatch = label.match(/^(\d+(?:\.\d+)?)\+$/);
  if (plusMatch) {
    return { group: 0, start: Number(plusMatch[1]), end: Number(plusMatch[1]) };
  }

  const numericMatch = label.match(/^(\d+(?:\.\d+)?)$/);
  if (numericMatch) {
    return { group: 2, start: Number(numericMatch[1]), end: Number(numericMatch[1]) };
  }

  return { group: 3, start: 0, end: 0 };
};

const getExperienceOptionLabel = (option) => (
  option?.label
  ?? option?.name
  ?? option?.experienceRange
  ?? option?.experience_range
  ?? option?.experience
  ?? option
);

const sortExperienceOptions = (options = [], getLabel = getExperienceOptionLabel) => (
  [...options].sort((first, second) => {
    const firstLabel = normalizeExperienceLabel(getLabel(first));
    const secondLabel = normalizeExperienceLabel(getLabel(second));
    const firstSort = getExperienceSortParts(firstLabel);
    const secondSort = getExperienceSortParts(secondLabel);

    return firstSort.group - secondSort.group
      || firstSort.start - secondSort.start
      || firstSort.end - secondSort.end
      || firstLabel.localeCompare(secondLabel, undefined, { numeric: true, sensitivity: 'base' });
  })
);

const buildExperienceOptionsForRole = (roleId, roleName, roleExperienceMappings = []) => {
  const selectedRoleId = normalizeText(roleId);
  const selectedRoleName = normalizeText(roleName).toLowerCase();

  if (!selectedRoleId && !selectedRoleName) return [];

  const experienceMap = new Map();
  roleExperienceMappings.forEach((mapping) => {
    const mappingRoleId = normalizeText(
      mapping.roleId
        ?? mapping.role_id
        ?? mapping.resourceTypeId
        ?? mapping.resource_type_id,
    );
    const mappingRoleName = normalizeText(
      mapping.roleName
        ?? mapping.role_name
        ?? mapping.resourceType
        ?? mapping.resource_type,
    ).toLowerCase();
    const isSelectedRole = (
      (selectedRoleId && mappingRoleId === selectedRoleId)
      || (selectedRoleName && mappingRoleName === selectedRoleName)
    );

    if (!isSelectedRole) return;

    const experienceLabel = normalizeText(
      mapping.experienceRange
        ?? mapping.experience_range
        ?? mapping.experienceName
        ?? mapping.experience_name
        ?? mapping.experience
        ?? mapping.label,
    );
    if (!experienceLabel) return;

    experienceMap.set(experienceLabel.toLowerCase(), {
      id: experienceLabel,
      name: experienceLabel,
    });
  });

  return sortExperienceOptions(Array.from(experienceMap.values()));
};

const buildProjectOptions = (projects, projectInfo, projectId) => {
  const mappedProjects = (projects || [])
    .filter((project) => project.value != null)
    .map((project) => ({
      ...project,
      id: String(project.value),
      name: project.label,
    }));

  if (mappedProjects.length) return mappedProjects;
  if (projectInfo?.project_name && projectId != null && projectId !== '') {
    return [{
      id: String(projectId),
      name: projectInfo.project_name,
      start_date: projectInfo.start_date || '',
      replanned_end_date: projectInfo.replanned_end_date || '',
    }];
  }

  return [];
};


const mapLabelOptions = (items = []) => items
  .filter((item) => item.value != null || item.label != null)
  .map((item) => ({
    id: String(item.value ?? item.label),
    name: item.label,
  }));

const RequestFormBody = ({
  projectOptions,
  verticalOptions,
  experienceOptions,
  roleOptions,
  selectedProject,
  selectedVertical,
  selectedExperience,
  selectedRole,
  draftRow,
  error,
  isProjectDisabled,
  onDraftChange,
}) => {
  const updateField = (field, value) => {
    onDraftChange({
      ...draftRow,
      [field]: value,
    });
  };

  const handleTextFieldChange = (event) => {
    updateField(event.target.id, event.target.value);
  };

  return (
    <div className="add-requirement-form sr-add-request-form">
      <div className="form-field sr-form-card sr-form-card--project">
        <SearchDropdown
          label={(
            <span>
              Project
              <span className="mandatory-star"> *</span>
            </span>
          )}
          options={projectOptions}
          value={selectedProject}
          placeholder="Select Project"
          dropdownCss="status-dropdown"
          clearIcon={!isProjectDisabled}
          disabled={isProjectDisabled}
          onChange={(event) => updateField('projectId', event.target.value?.id || '')}
        />
      </div>

      <div className="staff-aug-status sr-form-card sr-form-card--vertical">
        <SearchDropdown
          label={(
            <span>
              Employee Vertical
              <span className="mandatory-star"> *</span>
            </span>
          )}
          options={verticalOptions}
          value={selectedVertical}
          placeholder="Select"
          dropdownCss="status-dropdown"
          onChange={(event) => updateField('vertical', event.target.value?.name || '')}
        />
      </div>

      <div className="staff-aug-skills sr-form-card sr-form-card--role">
        <SearchDropdown
          label="Role"
          options={roleOptions}
          value={selectedRole}
          placeholder="Select"
          dropdownCss="status-dropdown"
          clearIcon
          onChange={(event) => {
            const selectedOption = event.target.value;
            onDraftChange({
              ...draftRow,
              roleId: selectedOption?.id ?? selectedOption?.value ?? '',
              roleName: selectedOption?.name ?? selectedOption?.label ?? '',
              experience: '',
            });
          }}
        />
      </div>

      <div className="staff-aug-skills sr-form-card sr-form-card--experience">
        <SearchDropdown
          label={(
            <span className="sr-experience-label">
              Experience
              <span className="mandatory-star"> *</span>
              <Tooltip
                title="Select a role to view experience options"
                arrow
                placement="top"
              >
                <InfoOutlinedIcon className="sr-experience-label__info" fontSize="inherit" />
              </Tooltip>
            </span>
          )}
          options={experienceOptions}
          value={selectedExperience}
          placeholder="Select"
          dropdownCss="status-dropdown"
          disabled={!draftRow.roleId && !draftRow.roleName}
          onChange={(event) => updateField('experience', event.target.value?.id || '')}
        />
      </div>

      <div className="staff-date-container sr-form-card sr-form-card--start-date">
        <CommonDatePicker
          label={(
            <span>
              Start Date
              <span className="mandatory-star"> *</span>
            </span>
          )}
          placeholder="Choose date"
          value={draftRow.startDate ? dayjs(draftRow.startDate) : null}
          onChange={(value) => updateField('startDate', value ? dayjs(value).format('YYYY-MM-DD') : '')}
          minDate={dayjs().startOf('day')}
          displayFormat="DD/MM/YYYY"
        />
      </div>

      <div className="staff-date-container sr-form-card sr-form-card--end-date">
        <CommonDatePicker
          label={(
            <span>
              End Date
              <span className="mandatory-star"> *</span>
            </span>
          )}
          placeholder="Choose date"
          value={draftRow.endDate ? dayjs(draftRow.endDate) : null}
          onChange={(value) => updateField('endDate', value ? dayjs(value).format('YYYY-MM-DD') : '')}
          minDate={draftRow.startDate ? dayjs(draftRow.startDate) : dayjs().startOf('day')}
          displayFormat="DD/MM/YYYY"
        />
      </div>

      <div className="staff-aug-skills sr-form-card sr-form-card--allocation">
        <CommonTextField
          label={(
            <span>
              Allocation
              <span className="mandatory-star"> *</span>
            </span>
          )}
          placeholder="Enter Allocation %"
          value={draftRow.allocation}
          onChange={handleTextFieldChange}
          variant="outlined"
          textCss="requirement-location-field"
          autoFocus={false}
          size="small"
          id="allocation"
          disabled={false}
        />
      </div>

      <div className="staff-aug-status sr-form-card sr-form-card--resources">
        <CommonTextField
          label={(
            <span>
              Number of Resources
              <span className="mandatory-star"> *</span>
            </span>
          )}
          placeholder="Enter Number of Resources"
          value={draftRow.resourceNo}
          onChange={handleTextFieldChange}
          variant="outlined"
          textCss="requirement-location-field"
          autoFocus={false}
          size="small"
          id="resourceNo"
          disabled={false}
        />
      </div>

      <div className="staff-aug-skills sr-form-card sr-form-card--mandatory-skill">
        <CommonTextField
          label={(
            <span>
              Mandatory Skill
              <span className="mandatory-star"> *</span>
            </span>
          )}
          placeholder="Enter Mandatory Skill"
          value={draftRow.mandatorySkills}
          onChange={handleTextFieldChange}
          variant="outlined"
          textCss="requirement-location-field"
          autoFocus={false}
          size="small"
          id="mandatorySkills"
          disabled={false}
        />
      </div>

      <div className="staff-aug-skills sr-form-card sr-form-card--good-skill">
        <CommonTextField
          label="Good to have Skill"
          placeholder="Enter Good to have Skill"
          value={draftRow.goodSkills}
          onChange={handleTextFieldChange}
          variant="outlined"
          textCss="requirement-location-field"
          autoFocus={false}
          size="small"
          id="goodSkills"
          disabled={false}
        />
      </div>

      <div className="description-text-field sr-form-card sr-form-card--comment">
        <CommonTextField
          label="Comment"
          placeholder="Enter..."
          value={draftRow.remarks}
          onChange={handleTextFieldChange}
          variant="outlined"
          textCss="requirement-location-field"
          autoFocus={false}
          size="small"
          id="remarks"
          disabled={false}
          multiline
          rows={1}
        />
      </div>

      {error ? <p className="sr-error-text sr-add-request-error">{error}</p> : null}
    </div>
  );
};

const NewRequestPanel = ({
  isOpen,
  projectInfo,
  currentSelectedProjectId,
  options,
  draftRow,
  error,
  isSubmitting,
  onDraftChange,
  onSubmit,
  onCancel,
}) => {
  const isProjectDisabled = currentSelectedProjectId != null && currentSelectedProjectId !== '';

  const projectOptions = useMemo(
    () => buildProjectOptions(options.projects, projectInfo, draftRow.projectId),
    [options.projects, projectInfo, draftRow.projectId],
  );

  const verticalOptions = useMemo(() => mapLabelOptions(options.verticals), [options.verticals]);

  const experienceOptions = useMemo(
    () => buildExperienceOptionsForRole(
      draftRow.roleId,
      draftRow.roleName,
      options.roleExperienceMappings,
    ),
    [draftRow.roleId, draftRow.roleName, options.roleExperienceMappings],
  );

  const roleOptions = useMemo(() => mapLabelOptions(options.roles), [options.roles]);

  const selectedProject = projectOptions.find(
    (option) => String(option.id) === String(draftRow.projectId),
  ) || null;

  const selectedVertical = verticalOptions.find(
    (option) => String(option.name) === String(draftRow.vertical),
  ) || null;

  const selectedExperience = experienceOptions.find(
    (option) => String(option.id) === String(draftRow.experience),
  ) || null;

  const selectedRole = roleOptions.find(
    (option) => String(option.id) === String(draftRow.roleId),
  ) || roleOptions.find(
    (option) => String(option.name).toLowerCase() === String(draftRow.roleName).toLowerCase(),
  ) || null;

  useEffect(() => {
    if (!isOpen || !isProjectDisabled) return;
    if (String(draftRow.projectId) === String(currentSelectedProjectId)) return;
    onDraftChange({ ...draftRow, projectId: currentSelectedProjectId });
    // Runs only when the panel opens or the pre-selected project changes; draftRow/onDraftChange intentionally excluded
  }, [isOpen, isProjectDisabled, currentSelectedProjectId]);

  useEffect(() => {
    if (!isOpen) return;
    const today = dayjs()
      .startOf('day')
      .format('YYYY-MM-DD');
    const nextDraftRow = { ...draftRow };
    let shouldUpdate = false;

    if (nextDraftRow.startDate && nextDraftRow.startDate < today) {
      nextDraftRow.startDate = '';
      shouldUpdate = true;
    }

    if (nextDraftRow.endDate && nextDraftRow.endDate < today) {
      nextDraftRow.endDate = '';
      shouldUpdate = true;
    }

    if (shouldUpdate) onDraftChange(nextDraftRow);
  }, [draftRow, isOpen, onDraftChange]);

  if (!isOpen) return null;

  return (
    <FormDialog
      title="Add Staffing Request"
      open={isOpen}
      handleClose={onCancel}
      button1Action={() => {
        onDraftChange(EMPTY_REQUEST_ROW);
        onCancel();
      }}
      button2Action={onSubmit}
      dialogPaperCss="staffing-request-dialog staffing-request-dialog--add"
      dialogBodycontent={(
        <RequestFormBody
          projectOptions={projectOptions}
          verticalOptions={verticalOptions}
          experienceOptions={experienceOptions}
          roleOptions={roleOptions}
          selectedProject={selectedProject}
          selectedVertical={selectedVertical}
          selectedExperience={selectedExperience}
          selectedRole={selectedRole}
          draftRow={draftRow}
          error={error}
          isProjectDisabled={isProjectDisabled}
          onDraftChange={onDraftChange}
        />
      )}
      button1="Cancel"
      button2="Add"
      cancelButtonId="staffing-request-add-cancel"
      submitButtonId="staffing-request-add-submit"
      button2Disabled={isSubmitting}
    />
  );
};

RequestFormBody.propTypes = {
  projectOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })).isRequired,
  verticalOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })).isRequired,
  experienceOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })).isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })).isRequired,
  selectedProject: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  selectedVertical: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  selectedExperience: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  selectedRole: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  draftRow: PropTypes.shape({
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vertical: PropTypes.string,
    experience: PropTypes.string,
    resourceNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    allocation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roleName: PropTypes.string,
    mandatorySkills: PropTypes.string,
    goodSkills: PropTypes.string,
    remarks: PropTypes.string,
  }).isRequired,
  error: PropTypes.string,
  isProjectDisabled: PropTypes.bool,
  onDraftChange: PropTypes.func.isRequired,
};

RequestFormBody.defaultProps = {
  selectedProject: null,
  selectedVertical: null,
  selectedExperience: null,
  selectedRole: null,
  error: '',
  isProjectDisabled: false,
};

NewRequestPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  projectInfo: PropTypes.shape({
    project_name: PropTypes.string,
    start_date: PropTypes.string,
    replanned_end_date: PropTypes.string,
  }).isRequired,
  currentSelectedProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.shape({
    verticals: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
      }),
    ),
    projects: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
      }),
    ),
    roles: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
      }),
    ),
    roleExperienceMappings: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  draftRow: PropTypes.shape({
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vertical: PropTypes.string,
    experience: PropTypes.string,
    resourceNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    allocation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roleName: PropTypes.string,
    mandatorySkills: PropTypes.string,
    goodSkills: PropTypes.string,
    remarks: PropTypes.string,
  }).isRequired,
  error: PropTypes.string,
  isSubmitting: PropTypes.bool,
  onDraftChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

NewRequestPanel.defaultProps = {
  error: '',
  isSubmitting: false,
  currentSelectedProjectId: null,
};

export default NewRequestPanel;
